<template>
  <PageLayout
    no-padding
    :reference-id="templateData.reference_id"
    :amount="formattedAmount"
    :currency="templateData.currency"
    :default-locale="templateData.default_locale">
    <template #default="{ logo }">
      <div class="blik-invoice-page__card" :class="{ 'blik-invoice-page__card--waiting': waiting }">
        <WaitingScreen v-if="waiting" @cancel="cancelPayment" />

        <template v-else>
          <h1 class="blik-invoice-page__title">{{ $t('blik_invoice__title') }}</h1>
          <p class="blik-invoice-page__subtitle">{{ $t('blik_invoice__subtitle') }}</p>

          <div v-if="hasRequisites" class="blik-invoice-page__requisites">
            <div class="blik-invoice-page__requisites-title">
              {{ $t('blik_invoice__requisites_title') }}
            </div>
            <div v-for="(item, key) in fields" :key="key" class="blik-invoice-page__field">
              <Field :value="item" :label="$t(`field__${key}`)" readonly>
                <template #prefix>
                  <ButtonCopy :value="item" is-gray />
                </template>
              </Field>
            </div>
          </div>

          <ConfirmationForm
            v-if="confirmationPayload && !!Object.keys(confirmationPayload).length"
            ref="formFields"
            :fields="confirmationPayload"
            :method="paymentInvoice.service_method" />

          <p v-if="isConfirmationPayloadInvalid" class="blik-invoice-page__error">
            {{ $t('blik_invoice__confirmation_invalid') }}
          </p>

          <p v-if="hasBlikCode" class="blik-invoice-page__hint">
            {{ $t('blik_invoice__code_hint') }}
          </p>

          <button
            :disabled="waiting || isConfirmationPayloadInvalid"
            class="blik-invoice-page__button"
            @click="confirm">
            {{ payButtonLabel }}
          </button>

          <button
            v-if="hasBlikCode"
            type="button"
            class="blik-invoice-page__help-link"
            @click="openHelp">
            {{ $t('blik_invoice__help_link') }}
          </button>
        </template>
      </div>

      <HelpModal :visible="helpOpen" :logo="logo" @close="helpOpen = false" />
    </template>
  </PageLayout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ButtonCopy from './components/ButtonCopy.vue';
import Field from './components/Field.vue';
import ConfirmationForm from './components/Form.vue';
import HelpModal from './components/HelpModal.vue';
import WaitingScreen from './components/WaitingScreen.vue';
import PageLayout from './components/PageLayout.vue';
import IframeEventService from '@/services/IframeEventService';
import formatInvoiceAmount from './formatAmount';

export default {
  name: 'BlikInvoicePage',
  components: { ButtonCopy, Field, ConfirmationForm, HelpModal, WaitingScreen, PageLayout },

  created() {
    this.startTimer(this.templateData.expires);
  },

  data() {
    return {
      waiting: false,
      helpOpen: false,
      paymentSubmitted: false,
    };
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData']),

    confirmationPayloadState() {
      const { payload } = (this.templateData || {}).confirmation || {};
      if (!payload) {
        return { value: payload, invalid: false };
      }
      if (typeof payload !== 'string') {
        return { value: payload, invalid: false };
      }
      try {
        return { value: JSON.parse(payload), invalid: false };
      } catch (e) {
        return { value: null, invalid: true };
      }
    },

    confirmationPayload() {
      return this.confirmationPayloadState.value;
    },

    isConfirmationPayloadInvalid() {
      return this.confirmationPayloadState.invalid;
    },

    hasBlikCode() {
      return !!(this.confirmationPayload && this.confirmationPayload.blik_code);
    },

    hasRequisites() {
      return Object.keys(this.fields).length > 0;
    },

    fields() {
      const objRequisites = this.templateData.requisites || {};
      const data = {
        bank_name: null,
        account_number: null,
        account_holder: null,
        document_type: null,
        document_id: null,
        bank_code: null,
        bank_ifsc_code: null,
        account_holder_address: null,
        bank_address: null,
        bic: null,
        purpose_of_payment: null,
        rif: null,
        phone: null,
        beneficiary: null,
        cbu: null,
        alias: null,
      };

      Object.keys(data).forEach(key => {
        if (key in objRequisites) {
          data[key] = objRequisites[key];
        }
      });

      Object.keys(data).forEach(key => {
        if (!data[key]) delete data[key];
      });

      return data;
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    formattedAmount() {
      return formatInvoiceAmount(this.templateData.amount, this.templateData.currency);
    },

    payButtonLabel() {
      return this.$t('blik_invoice__pay_button', {
        amount: this.formattedAmount,
        currency: this.templateData.currency,
      });
    },

    templateData() {
      const { reference_id, description, has_return_url, id, expires } = this.paymentInvoice;
      const { amount: mainAmount, currency: mainCurrency } = this.flowData;
      const {
        confirmation,
        default_locale = null,
        method,
        requisites,
        amount: payloadAmount,
        currency: payloadCurrency,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) {
          return {};
        }

        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch (e) {
            return {};
          }
        }

        return requisites;
      };

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;
      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      return {
        id,
        reference_id,
        description,
        amount,
        currency,
        logo: (method || {}).logo || null,
        expires,
        has_return_url,
        confirmation,
        default_locale,
        requisites: parseRequisites(),
      };
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),
    ...mapActions('InvoiceLifetime', ['startTimer']),

    async confirm() {
      let data = {};

      if (this.waiting) {
        return;
      }

      if (this.paymentSubmitted) {
        this.waiting = true;
        return;
      }

      if (this.isConfirmationPayloadInvalid) {
        return;
      }

      if (
        !(this.templateData.confirmation && Object.keys(this.templateData.confirmation).length) ||
        !this.templateData.confirmation.required
      ) {
        this.redirectReturnUrl();
        return;
      }

      if (this.confirmationPayload && !!Object.keys(this.confirmationPayload).length) {
        const resp = await this.$refs.formFields.submit();

        if (resp.valid) {
          data = { payload: { ...resp.form } };
        } else {
          return;
        }
      }

      this.waiting = true;

      try {
        const { status } = await this.confirmPaymentInvoice({ id: this.templateData.id, data });

        if (status !== 200) {
          this.waiting = false;
        } else {
          this.paymentSubmitted = true;
        }
      } catch (e) {
        this.waiting = false;
      }
    },

    cancelPayment() {
      if (this.paymentInvoice.has_return_url) {
        this.redirectReturnUrl(true);
        return;
      }
      this.waiting = false;
    },

    openHelp() {
      this.helpOpen = true;
    },

    redirectReturnUrl(force) {
      if (!this.templateData.has_return_url) {
        return;
      }

      const isForce = force ? '&force=true' : '';
      const returnUrl = `/return?id=${this.paymentInvoice.id}${isForce}`;
      IframeEventService.emitRedirect(window.location.origin + returnUrl);
      location.replace(returnUrl);
    },
  },
};
</script>

<style lang="scss">
@import './styles';

.blik-invoice-page {
  .el-form-item {
    margin-bottom: 0;
  }

  .el-form-item__error {
    justify-content: right;
  }

  .el-form-item__label {
    font-size: 14px;
    line-height: 16px;
    color: #5b5b61;
    margin-bottom: 10px;
  }

  &__requisites {
    margin-bottom: 10px;
  }

  &__requisites-title {
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 10px;
    color: #5b5b61;
  }

  &__hint {
    font-size: 13px;
    line-height: 15px;
    text-align: left;
    margin: 2px 0 20px;
    color: #77777d;
  }

  &__error {
    font-size: 13px;
    line-height: 18px;
    text-align: left;
    margin: 0 0 16px;
    color: #9e2634;
  }

  &__button {
    display: block;
    width: 100%;
    height: 50px;
    border: 0;
    border-radius: 10px;
    color: #ffffff;
    background: linear-gradient(160deg, #d83d20 8.49%, #be2d39 48.34%, #9d346d 91.51%);
    font-family: 'Arial';
    font-size: 15px;
    line-height: 18px;
    font-weight: 700;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: opacity 0.2s, filter 0.2s;
    margin-bottom: 17px;

    &:hover:not(:disabled) {
      filter: brightness(1.04);
    }

    &:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }
  }

  &__help-link {
    font-size: 14px;
    line-height: 16px;
    outline: none;
    background: none;
    border: none;
    color: #f06b7a;
    cursor: pointer;
  }

  &__footer {
    width: 100%;
    margin-top: 16px;
  }

  &__footer-back {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    cursor: pointer;

    img {
      height: 7px;
    }
  }
}

html[data-color-scheme='dark'] {
  .blik-invoice-page {
    .el-form-item__label {
      color: #e0e0e0;
    }

    &__requisites-title,
    &__footer-back {
      color: #e0e0e0;
    }

    &__hint {
      color: #b5b5b5;
    }

    &__error {
      color: #ffb6bb;
    }

    &__help-link {
      color: #f06b7a;
    }

    &__button {
      background: linear-gradient(160deg, #d83d20 8.49%, #be2d39 48.34%, #9d346d 91.51%);
      color: #ffffff;
    }
  }
}
</style>
